import type { ChannexQuarantinedRevision } from '@/shared/api';
import { formatDateRange, formatDateTime } from '@/shared/lib/format';
import { holdReason, revisionStatusLabel } from './quarantineLabels';
import { channelLabel } from './revisionHoldPresentation';

/** Eine Frist nur, solange der Server selbst versucht und sie noch läuft. */
export function waitText(row: ChannexQuarantinedRevision, automaticRetry: boolean): string {
  if (!automaticRetry) {
    return 'Die automatische Wiederholung ist ausgeschaltet — die Buchung wartet, bis sie wieder läuft.';
  }
  return row.waitDeadline
    ? `Wird automatisch erneut versucht, spätestens bis ${formatDateTime(row.waitDeadline)} — danach steht sie hier in der Quarantäne.`
    : 'Wird gleich automatisch erneut versucht; scheitert das, steht sie hier in der Quarantäne.';
}

/**
 * Derselbe Grund steht schon darüber — dann genügt die Zahl der Versuche. Kann eine Revision in
 * der Quarantäne nicht erneut verarbeitet werden, nennt die Zeile darunter, worauf sie wartete.
 */
export function attemptsText(row: ChannexQuarantinedRevision): string {
  const attempts = row.replayAttempts === 1 ? '1 Versuch' : `${row.replayAttempts} Versuche`;
  if (row.lastReplayError === row.quarantineCode) return `${attempts}, zuletzt aus demselben Grund`;
  const label = row.processingState === 'Quarantined' && !row.canReplay ? 'zuvor' : 'zuletzt';
  return `${attempts} · ${label}: ${holdReason(row.lastReplayError, row)}`;
}

export function title(row: ChannexQuarantinedRevision): string {
  if (!row.details) return 'Kanalbuchung';
  const channel = channelLabel(row.details.channel);
  return row.details.otaReservationCode ? `${channel} ${row.details.otaReservationCode}` : channel;
}

export function facts(row: ChannexQuarantinedRevision): string[] {
  const stay =
    row.details?.arrivalDate && row.details.departureDate
      ? formatDateRange(row.details.arrivalDate, row.details.departureDate)
      : null;
  return [
    row.propertyGroupName ?? 'ohne Objekt',
    stay ?? (row.details ? 'Zeitraum unbekannt' : 'Angaben zur Buchung nicht lesbar'),
    revisionStatusLabel(row.revisionStatus),
    `eingegangen ${formatDateTime(row.receivedAt)}`,
  ];
}
